import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "./_utils";

export const Select = SelectPrimitive.Root;
export const SelectGroup = SelectPrimitive.Group;
export const SelectValue = SelectPrimitive.Value;

export function SelectTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger>) {
  return (
    <SelectPrimitive.Trigger
      className={cn(
        "flex h-9 w-full items-center justify-between gap-sm rounded-[var(--radius-control)] " +
          "border border-border bg-bg-input px-md py-xs text-base " +
          "text-text-primary placeholder:text-text-tertiary " +
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus " +
          "disabled:cursor-not-allowed disabled:opacity-50 " +
          "data-[placeholder]:text-text-tertiary",
        className
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="h-4 w-4 opacity-60" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

export function SelectContent({
  className,
  children,
  position = "popper",
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        position={position}
        className={cn(
          // Glass: the popup is a transient overlay floating over the field.
          "relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-[var(--radius-card)] " +
            "border border-[var(--glass-border)] bg-[var(--glass-bg)] " +
            "[-webkit-backdrop-filter:blur(var(--glass-blur))_saturate(1.15)] " +
            "[backdrop-filter:blur(var(--glass-blur))_saturate(1.15)] " +
            "text-text-primary shadow-[var(--shadow-overlay)]",
          position === "popper" &&
            "data-[side=bottom]:translate-y-xs data-[side=top]:-translate-y-xs",
          className
        )}
        {...props}
      >
        <SelectPrimitive.Viewport
          className={cn(
            "p-xs",
            position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

export function SelectLabel({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      className={cn("px-sm py-xs text-xs text-text-secondary", className)}
      {...props}
    />
  );
}

export function SelectItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      className={cn(
        "relative flex w-full cursor-pointer select-none items-center rounded-[var(--radius-control)] " +
          "py-xs pl-xl pr-sm text-sm outline-none " +
          // Hover on glass is a white bump, never a color shift.
          "focus:bg-[rgba(var(--glass-rgb),0.65)] data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      )}
      {...props}
    >
      <span className="absolute left-sm flex h-4 w-4 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="h-4 w-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

export function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      className={cn("-mx-xs my-xs h-px bg-[var(--glass-divider)]", className)}
      {...props}
    />
  );
}

/**
 * Select (combobox) built on Radix Select.
 *
 * @example
 * <Select defaultValue="apple" onValueChange={setFruit}>
 *   <SelectTrigger><SelectValue placeholder="Pick one" /></SelectTrigger>
 *   <SelectContent>
 *     <SelectItem value="apple">Apple</SelectItem>
 *     <SelectItem value="banana">Banana</SelectItem>
 *   </SelectContent>
 * </Select>
 */
